import React from 'react';
import {
  FormControl,
  MenuItem,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  Select,
} from '@mui/material';
import { useCenterListQuery } from '../../../../services/orgService/org-api';

interface FacilitySelectorProps {
  selectedLocations: string[];
  onChange: (locations: string[]) => void;
  multiple?: boolean;
  ticketType?: string;
}

const FacilitySelector: React.FC<FacilitySelectorProps> = ({ 
  selectedLocations, 
  onChange, 
  multiple = true,
  ticketType,
}) => {
  const { data: centers = [] } = useCenterListQuery();

  const handleLocationChange = (event: SelectChangeEvent<string[]>) => {
    const locations = typeof event.target.value === 'string'
      ? [event.target.value]
      : event.target.value;
    onChange(locations);
  };

  const handleSingleLocationChange = (event: SelectChangeEvent<string>) => {
    onChange([event.target.value as string]);
  };  // Filter centers based on ticket type
  const filteredCenters = React.useMemo(() => {
    if (!centers || centers.length === 0) return [];
    
    let filtered = centers.filter(center => !center.isInactive);
    
    // Apply additional filtering based on ticket type
    if (ticketType === 'HST' || ticketType === 'CPSI') {
      filtered = filtered.filter(center => {
        // Handle pas as either a string or an object with value property
        let pasValue = '';
        if (typeof center.pas === 'string') {
          pasValue = center.pas;
        } else if (center.pas && typeof center.pas === 'object') {
          pasValue = center.pas || '';
        }
        
        return pasValue.toUpperCase() === ticketType.toUpperCase();
      });
    }
    
    // If no centers match the filter, return all active centers as fallback
    if (filtered.length === 0 && ticketType) {
      filtered = centers.filter(center => !center.isInactive);
    }
    
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [centers, ticketType]);

  return (
    <FormControl fullWidth>
      {multiple ? (
        <Select
          sx={{ marginTop: 1 }}
          labelId="locations-label"
          multiple
          value={selectedLocations}
          onChange={handleLocationChange}
          renderValue={(selected) => selected.join(', ')}
          size="small"
          variant="outlined"
        >
          {filteredCenters.map((center) => (
            <MenuItem key={center.id} value={center.name}>
              <Checkbox checked={selectedLocations.indexOf(center.name) > -1} />
              <ListItemText primary={center.name} />
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Select
          labelId="locations-label"
          value={selectedLocations[0] || ''}
          onChange={handleSingleLocationChange}
          size="small"
          variant="outlined"
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {filteredCenters.map((center) => (
            <MenuItem key={center.id} value={center.name}>
              {center.name}
            </MenuItem>
          ))}
        </Select>
      )}
    </FormControl>
  );
};

export { FacilitySelector };